import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import MainLayout from '../layouts/MainLayout';
import { Plus, Edit, Trash2 } from 'lucide-react';
import * as blogService from '../services/blogService';

interface PageContentItem {
  id?: string;
  _id?: string;
  slug?: string;
  title: {
    tr: string;
    en: string;
    es: string;
  };
  content: {
    tr: string;
    en: string;
    es: string;
  };
  tags?: string[];
  author?: string;
  status?: string;
  createdAt?: string;
  updatedAt?: string;
}

const PageManagement: React.FC = () => {
  const [pages, setPages] = useState<PageContentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPage, setEditingPage] = useState<PageContentItem | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<'tr' | 'en' | 'es'>('tr');
  const [submitting, setSubmitting] = useState(false);

  // Sayfa içeriklerini API'den yükle (blog API'sini kullan, page-content tag'i ile filtrele)
  const fetchPages = async () => {
    try {
      setLoading(true);
      const blogsData = await blogService.getBlogs();
      console.log('Blogs received for pages:', blogsData);
      // Sadece page-content tag'ine sahip blog'ları al
      const pageData = blogsData.filter((blog: any) => 
        blog.tags && blog.tags.includes('page-content')
      );
      setPages(pageData);
    } catch (error) {
      console.error('Error fetching pages:', error);
      toast.error('Sayfa içerikleri yüklenirken hata oluştu');
      setPages([]); // Empty fallback
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPages();
  }, []);

  const handleEdit = (page: PageContentItem) => {
    // Normalize page data
    const normalizedPage = {
      ...page,
      id: page.id || page._id,
      title: page.title || { tr: '', en: '', es: '' },
      content: page.content || { tr: '', en: '', es: '' },
      tags: page.tags || ['page-content'],
      author: page.author || 'Admin',
      status: page.status || 'published'
    };
    setEditingPage(normalizedPage);
    setShowForm(true);
  };

  const handleNewPage = () => {
    setEditingPage(null);
    setShowForm(true);
  };

  const handleDelete = async (page: PageContentItem) => {
    if (window.confirm('Bu sayfa içeriğini silmek istediğinizden emin misiniz?')) {
      try {
        const pageId = page.id || page._id;
        if (!pageId) {
          toast.error('Sayfa ID bulunamadı');
          return;
        }
        await blogService.deleteBlog(pageId);
        toast.success('Sayfa içeriği başarıyla silindi');
        fetchPages();
      } catch (error) {
        console.error('Error deleting page:', error);
        toast.error('Sayfa silinirken hata oluştu');
      }
    }
  };

  const handleSubmit = async (formData: FormData) => {
    try {
      setSubmitting(true);
      
      // Tags'e page-content ekle
      const tags = (formData.get('tags') as string || '').split(',').map(tag => tag.trim()).filter(tag => tag);
      if (!tags.includes('page-content')) {
        tags.push('page-content');
      }
      
      const pageData = {
        title: {
          tr: formData.get('title_tr') as string || '',
          en: formData.get('title_en') as string || '',
          es: formData.get('title_es') as string || '',
        },
        content: {
          tr: formData.get('content_tr') as string || '',
          en: formData.get('content_en') as string || '',
          es: formData.get('content_es') as string || '',
        },
        tags: tags,
        author: formData.get('author') as string || 'Admin',
        status: 'published' // Sayfa içerikleri her zaman published
      };

      if (editingPage) {
        const pageId = editingPage.id || editingPage._id;
        if (!pageId) {
          toast.error('Sayfa ID bulunamadı');
          return;
        }
        await blogService.updateBlog(pageId, pageData);
        toast.success('Sayfa içeriği başarıyla güncellendi');
      } else {
        await blogService.createBlog(pageData);
        toast.success('Sayfa içeriği başarıyla oluşturuldu');
      }
      
      setShowForm(false);
      setEditingPage(null);
      fetchPages();
    } catch (error: any) {
      console.error('Error saving page:', error);
      toast.error('Sayfa kaydedilirken hata oluştu');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Sayfa Yönetimi</h1>
              <p className="text-gray-600 mt-1">Website sayfa içeriklerini yönetin</p>
            </div>
            <button
              onClick={handleNewPage}
              className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 flex items-center space-x-2"
            >
              <Plus className="h-4 w-4" />
              <span>Yeni Sayfa</span>
            </button>
          </div>
        </div>

        {/* Pages List */}
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Sayfa İçerikleri</h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sayfa Başlığı
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Son Güncelleme
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    İşlemler
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={3} className="px-6 py-12 text-center text-gray-500">
                      Sayfa içerikleri yükleniyor...
                    </td>
                  </tr>
                ) : pages.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="px-6 py-12 text-center text-gray-500">
                      Henüz sayfa içeriği eklenmemiş
                    </td>
                  </tr>
                ) : pages.map((page) => (
                  <tr key={page.id || page._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {page.title?.tr || 'Başlık yok'}
                        </div>
                        <div className="text-sm text-gray-500 truncate max-w-xs">
                          {page.content?.tr?.substring(0, 100) || 'İçerik yok'}...
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {page.updatedAt ? new Date(page.updatedAt).toLocaleDateString('tr-TR') : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleEdit(page)}
                          className="text-primary-600 hover:text-primary-900"
                          title="Düzenle"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(page)}
                          className="text-red-600 hover:text-red-900"
                          title="Sil"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {editingPage ? 'Sayfa Düzenle' : 'Yeni Sayfa'}
                  </h3>
                  <button
                    onClick={() => setShowForm(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <Plus className="h-6 w-6 transform rotate-45" />
                  </button>
                </div>
              </div>

              {/* Language Tabs */}
              <div className="px-6 pt-4">
                <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
                  {['tr', 'en', 'es'].map((lang) => (
                    <button
                      key={lang}
                      onClick={() => setSelectedLanguage(lang as 'tr' | 'en' | 'es')}
                      className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                        selectedLanguage === lang
                          ? 'bg-white text-primary-600 shadow-sm'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      {lang.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>

              <form 
                onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.currentTarget);
                  handleSubmit(formData);
                }}
                className="p-6 space-y-6"
              >
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sayfa Başlığı ({selectedLanguage.toUpperCase()})
                  </label>
                  <input
                    type="text"
                    name={`title_${selectedLanguage}`}
                    defaultValue={editingPage?.title?.[selectedLanguage] || ''}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Sayfa başlığını girin"
                  />
                </div>

                {/* Content */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    İçerik ({selectedLanguage.toUpperCase()})
                  </label>
                  <textarea
                    rows={10}
                    name={`content_${selectedLanguage}`}
                    defaultValue={editingPage?.content?.[selectedLanguage] || ''}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Sayfa içeriğini girin (HTML desteklenir)"
                  />
                </div>

                {/* Tags */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Etiketler
                  </label>
                  <input
                    type="text"
                    name="tags"
                    defaultValue={editingPage?.tags?.filter(tag => tag !== 'page-content').join(', ') || ''}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Etiketleri virgülle ayırarak yazın (örn: home, hero, banner)"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    'page-content' etiketi otomatik olarak eklenecektir
                  </p>
                </div>

                {/* Author */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Yazar
                  </label>
                  <input
                    type="text"
                    name="author"
                    defaultValue={editingPage?.author || 'Admin'}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Yazar adını girin"
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? 'Kaydediliyor...' : (editingPage ? 'Güncelle' : 'Kaydet')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default PageManagement;